import {
  CustomTextFieldInput,
  ModalPopup,
  AutocompleteInput,
  CustomFormControl,
} from "_components";
import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Box, TextField } from "@mui/material";
import {
  postPledgeAmountApi,
  getAgencyListApi,
  getEmergencyHoldStatusApi,
} from "services/energyAssistance/energyAssistance.thunks";
import { useDispatch } from "react-redux";
import { ERR_MSG } from "constants/labels";
import { alertActions } from "_store";
import { getPledgeAmountFormFields } from "container/energyAssistance/utils/pledgeEmergencyHoldRules";

const PledgeAmountModal = ({
  setOpenModal: closeParentModal,
  AccountNumber,
  Jurisdiction,
  PledgeAmountDetail,
}) => {
  const pledgeAmtText = "Pledge Amount";
  const cancelText = "Cancel";
  const defaultValues = { AgencyId: "" };
  const [openModal, setOpenModal] = useState(true);
  const [agency, setAgency] = useState([]);
  const [isPrimaryDisabled, setIsPrimaryDisabled] = useState(true);
  const [openPartialModal, setOpenPartialModal] = useState(false);
  const [partialMessage, setPartialMessage] = useState("");
  const dispatch = useDispatch();

  const fetchAgencyList = async () => {
    dispatch(alertActions.clear());
    try {
      const res = await dispatch(getAgencyListApi(AccountNumber)).unwrap();
      const agencyList = res?.Data?.map((data) => {
        return { label: data?.AgencyName, value: data?.AgencyId };
      });
      setAgency(agencyList);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Pledge Amount",
        })
      );
    }
  };

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
    register,
  } = useForm({ defaultValues });

  const agencyId = watch("AgencyId");
  const formValues = watch();

  const formFields = useMemo(
    () =>
      getPledgeAmountFormFields({
        agencyId,
        agencies: agency,
        Jurisdiction,
        PledgeAmountDetail,
      }),
    [agencyId, agency, Jurisdiction, PledgeAmountDetail]
  );

  useEffect(() => {
    const isMissingAgency = agency?.length > 1 && !agencyId;
    const editableFields = formFields?.fields.filter((f) => !f.disabled) || [];
    const filledCount = editableFields.filter(
      (f) => !!formValues[f.name]
    ).length;
    const isMissingAmount = !formFields || filledCount < 1;

    setIsPrimaryDisabled(isMissingAgency || isMissingAmount);
  }, [formFields, formValues, agencyId, agency]);

  useEffect(() => {
    fetchAgencyList();
    handleReset();
  }, []);

  const handleReset = () => reset(defaultValues);

  const handleCloseModal = () => {
    setAgency([]);
    setOpenModal(false);
    setOpenPartialModal(false);
    closeParentModal(false);
  };

  const buildPayload = (data, Partial) => {
    const PledgeAmountRequests = (formFields?.fields || [])
      .filter((f) => !f.disabled && !!data[f.name])
      .map((f) => ({
        PledgeAmount: data[f.name],
        DunningLockType: f.dunningLockType,
      }));
    return {
      AccountNumber,
      AgencyId: agency.length > 1 ? data.AgencyId : agency?.[0]?.value,
      PledgeAmountRequests,
      Partial,
    };
  };

  const onSubmit = async (data) => {
    dispatch(alertActions.clear());
    try {
      const payload = buildPayload(data, 0);
      const res = await dispatch(postPledgeAmountApi(payload)).unwrap();

      if (res?.Succeeded && res?.Data?.Status === "SUCCESSFUL") {
        dispatch(
          alertActions.success({
            message: res?.Data?.Message || "Pledge Amount Successful",
            header: pledgeAmtText,
          }),
        );
        dispatch(getEmergencyHoldStatusApi(AccountNumber));
        handleReset();
        handleCloseModal();
      } else if (res?.Succeeded && res?.Data?.Status === "PARTIAL") {
        setOpenModal(false);
        setPartialMessage(res?.Data?.Message);
        setOpenPartialModal(true);
      } else if (!res?.Succeeded) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: pledgeAmtText,
          }),
        );
        handleReset();
        handleCloseModal();
      }
    } catch (error) {
      handleReset();
      handleCloseModal();
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: pledgeAmtText,
        }),
      );
    }
  };

  const handleRetrySubmit = async () => {
    dispatch(alertActions.clear());
    try {
      const payload = buildPayload(watch(), 1);
      const res = await dispatch(postPledgeAmountApi(payload)).unwrap();

      if (res?.Succeeded && res?.Data?.Status === "SUCCESSFUL") {
        dispatch(
          alertActions.success({
            message: res?.Data?.Message || "Pledge Amount Successful",
            header: pledgeAmtText,
          })
        );
        dispatch(getEmergencyHoldStatusApi(AccountNumber));
        handleCloseModal();
      } else if (res?.Succeeded && res?.Data?.Status === "PARTIAL") {
        setPartialMessage(res?.Data?.Message);
      } else if (!res?.Succeeded) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: pledgeAmtText,
          })
        );
        handleCloseModal();
      }
    } catch (error) {
      handleCloseModal();
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: pledgeAmtText,
        })
      );
    }
  };

  const renderAmountFields = () => {
    if (!formFields) return null;
    return formFields.fields.map((field) =>
      field.disabled ? (
        <TextField
          key={field.name}
          label={field.label}
          value={field.existingAmount ?? ""}
          disabled
          margin="normal"
          fullWidth
        />
      ) : (
        <CustomFormControl
          key={field.name}
          required={!formFields.isDual}
          id={field.name}
          label={field.label}
          type="number"
          register={register}
          errors={errors}
        />
      )
    );
  };

  const renderForm = () => (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="Registrationcontainer marketerFiltercontainer"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        {agency?.length > 1 ? (
          <AutocompleteInput
            control={control}
            name="AgencyId"
            label="Select Agency"
            options={agency}
            forModalPopup={true}
          />
        ) : null}
        {renderAmountFields()}
      </Box>
    </form>
  );

  return (
    agency.length > 0 && (
      <>
        {openModal && (
          <ModalPopup
            header={pledgeAmtText}
            message1={renderForm()}
            btnPrimaryText={pledgeAmtText}
            btnSecondaryText={cancelText}
            handlePrimaryClick={handleSubmit(onSubmit)}
            handleSecondaryClick={handleCloseModal}
            isPrimaryDisable={isPrimaryDisabled}
          />
        )}
        {openPartialModal && (
          <ModalPopup
            header={pledgeAmtText}
            message1={partialMessage}
            btnPrimaryText={"RETRY"}
            btnSecondaryText={cancelText}
            handlePrimaryClick={handleRetrySubmit}
            handleSecondaryClick={handleCloseModal}
            errors={true}
          />
        )}
      </>
    )
  );
};

export default PledgeAmountModal;
